/* eslint-disable indent */
import { Timeline } from 'primereact/timeline';
import './style.scss';
import { useParams } from 'react-router-dom';
import React, { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import { useGetProjectProposalTimeLine } from 'utils/hooks/projectProposals';
import { formatDatetime } from 'utils/func';
import { COUNCIL_TYPE_CODE, PROJECT_STATUSES } from 'constant';

function TimeLine() {
  const { t } = useTranslation();
  const { projectProposalId } = useParams();

  const [isShowTimeline, setIsShowTimeline] = useState(true);
  const [shouldRender, setShouldRender] = useState(true);

  const { data, isLoading } = useGetProjectProposalTimeLine(projectProposalId, {});

  const handleToggleTimeline = () => {
    if (isShowTimeline) {
      // Start hide animation
      setIsShowTimeline(false);
      // Hide after animation completes (500ms)
      setTimeout(() => {
        setShouldRender(false);
      }, 500);
    } else {
      // Show timeline
      setShouldRender(true);
      setIsShowTimeline(true);
    }
  };

  useEffect(() => {
    // Reset states when showing
    if (isShowTimeline && !shouldRender) {
      setShouldRender(true);
    }
  }, [isShowTimeline, shouldRender]);

  const statusMapping = {
    notReviewed: {
      color: 'var(--cyan-600)',
      icon: 'pi pi-clock',
    },
    notQualified: {
      color: 'var(--red-600)',
      icon: 'pi pi-times',
    },
    qualified: {
      color: 'var(--green-600)',
      icon: 'pi pi-check',
    },
  };

  const defaultEvent = useMemo(
    () => [
      {
        label: `${t(`councilTypes.${COUNCIL_TYPE_CODE.PRELIMINARY_SELECTION_COUNCIL}`)}`,
        time: null,
        icon: null,
        color: 'var(--gray-500)',
      },
      {
        label: `${t(
          `councilTypes.${
            !data?.shortenedReview
              ? COUNCIL_TYPE_CODE.SPECIALIZATION_COUNCIL
              : COUNCIL_TYPE_CODE.SPECIALIZATION_AND_FINANCE_COUNCIL
          }`
        )}`,
        time: null,
        icon: null,
        color: 'var(--gray-500)',
      },
      ...(data?.shortenedReview
        ? []
        : [
            {
              label: `${t(`councilTypes.${COUNCIL_TYPE_CODE.FINANCE_COUNCIL}`)}`,
              time: null,
              icon: null,
              color: 'var(--gray-500)',
            },
          ]),

      {
        label: `${t('projectProposal.timeline.contract')}`,
        time: null,
        icon: null,
        color: 'var(--gray-500)',
      },
    ],
    [data, t]
  );

  const events = useMemo(() => {
    const result = [];

    if (data?.submittedAt) {
      result.push({
        label: `${t('projectProposal.timeline.submitted')}`,
        time: formatDatetime(data.submittedAt, 'DD/MM/YYYY HH:mm'),
        icon: 'pi pi-check',
        color: 'var(--green-600)',
      });
    } else {
      result.push({
        label: `${t('projectProposal.timeline.notSubmitted')}`,
        time: null,
        icon: 'pi pi-pencil',
        color: 'var(--yellow-600)',
      });
    }

    let projectObj = null;
    let isPushedProjectObj = false;
    if (data?.project) {
      projectObj =
        data.project.projectStatus?.name === PROJECT_STATUSES.DOING
          ? {
              label: `${t('projectProposal.timeline.projectSigned')}`,
              icon: 'pi pi-check',
              color: 'var(--green-600)',
              time: formatDatetime(data.project.signingDate, 'DD/MM/YYYY HH:mm'),
            }
          : {
              label: `${t('projectProposal.timeline.projectNotSigned')}`,
              icon: 'pi pi-times',
              color: 'var(--yellow-600)',
              time: null,
            };
    }

    if (data?.project && data?.projectProposalCouncils?.length === 0) {
      result[1] = projectObj;
    } else {
      const startCouncilOrder = data?.projectProposalCouncils?.[0]?.councilType?.order || 1;

      if (startCouncilOrder === 1) {
        result.push(...defaultEvent);
      }

      if (data?.projectProposalCouncils?.length > 0) {
        data.projectProposalCouncils.forEach((council, index) => {
          if (startCouncilOrder >= 4 && !isPushedProjectObj) {
            result[1] = projectObj;
            isPushedProjectObj = true;
          }
          if (!isPushedProjectObj && council?.councilType?.order === 4 && data?.project) {
            result[result.length - 1] = projectObj;
            isPushedProjectObj = true;
          }
          if (council?.councilType?.order <= 3) {
            if (council?.councilType?.order === 3 && data?.shortenedReview) {
              return;
            }
            result[index + 1] = {
              label: `${t(`councilTypes.${council.councilType?.code}`)}`,
              time: formatDatetime(council?.timeMet, 'DD/MM/YYYY HH:mm'),
              icon: statusMapping[council?.councilStatus]?.icon,
              color: statusMapping[council?.councilStatus]?.color,
            };
          }
          if (council?.councilType?.order >= 4) {
            result.push({
              label: `${t(`councilTypes.${council.councilType?.code}`)}`,
              time: formatDatetime(council?.timeMet, 'DD/MM/YYYY HH:mm'),
              icon: statusMapping[council?.councilStatus]?.icon,
              color: statusMapping[council?.councilStatus]?.color,
            });
          }
          if (data?.shortenedReview && council?.councilType?.order === 2) {
            result[2].label = `${t('councilTypes.specializationAndFinanceCouncil')}`;
          }
        });
      }
      if (!isPushedProjectObj && data?.project) {
        result[result.length - 1] = projectObj;
      }
    }

    if (data?.project && data?.project?.isCompleted) {
      result.push({
        label: `${t('projectProposal.timeline.projectCompleted')}`,
        time: null,
        icon: 'pi pi-check',
        color: 'var(--green-600)',
      });
    }

    return result;
  }, [data, defaultEvent, t]);
  const formatContent = (item) => <strong>{item.label}</strong>;

  const formatOpposite = (item) => <span>{item?.time}</span>;

  const customizedMarker = (item) => (
    <span
      className="flex flex-shrink-0 w-2rem h-2rem align-items-center justify-content-center text-white border-circle z-1 shadow-1"
      style={{ backgroundColor: item.color }}
    >
      <i className={item.icon} />
    </span>
  );

  return (
    <div className="flex">
      <Button
        icon={isShowTimeline ? 'pi pi-angle-double-left' : 'pi pi-angle-double-right'}
        iconPos="right"
        className={classNames('flex-shrink-0 pl-2', { 'p-2': !isShowTimeline })}
        text
        onClick={handleToggleTimeline}
        tooltip={
          isShowTimeline
            ? t('projectProposal.timeline.hideTimeLine')
            : t('projectProposal.timeline.showTimeLine')
        }

        // ref={toggleBtnRef}
      />

      {shouldRender && !isLoading && (
        <Timeline
          value={events}
          layout="horizontal"
          align="top"
          content={formatContent}
          opposite={formatOpposite}
          marker={customizedMarker}
          pt={({ options }) => ({
            connector: {
              style: {
                backgroundColor: events[options?.index]?.color || 'var(--gray-500)',
              },
            },
          })}
          className={`project-proposal-timeline px-4 md:px-8 overflow-x-auto animation-duration-500 ${
            isShowTimeline ? 'scalein animation-ease-in-out' : 'fadeout animation-ease-out'
          }`}
        />
      )}
    </div>
  );
}

export default React.memo(TimeLine);